import * as React from 'react';
import Helmet from 'react-helmet';
import DevsiteEnv from '../DevsiteEnv';

const suffix = ' | Workday Developers';
const separator = ' - ';

/**
 * True when joining to the site origin is required (absolute and protocol-relative URLs do not).
 */
function hrefNeedsSiteBase(href: string): boolean {
  const trimmed = href.trim();
  if (!trimmed) {
    return false;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return false;
  }
  if (trimmed.startsWith('//')) {
    return false;
  }
  return true;
}

/**
 * Serializes JSON-LD for inline script tags so `</script>` sequences in CMS strings cannot break out of the tag.
 */
function serializeJsonLd(data: Record<string, unknown>): string {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

function jsonLdKey(block: Record<string, unknown>): string {
  return typeof block['@type'] === 'string' ? block['@type'] : 'json-ld';
}

/**
 * Resolves CMS paths (e.g. `/cms/public/...`, `cms/public/...`) and other relative URLs to an absolute URL for meta tags.
 * Returns undefined when a relative href cannot be resolved because `baseUrl` is missing.
 */
function resolveDevsiteAbsoluteUrl(href: string, baseUrl: string | undefined): string | undefined {
  const trimmed = href.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`;
  }
  if (!baseUrl) {
    return undefined;
  }
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${baseUrl}${path}`;
}

interface DevsiteMetadataProps {
  title: string;

  /**
   * Optionally used to construct the page title (i.e. rather than "My App", would be "My App | Parent Page").
   */
  parentTitle?: string;

  /**
   * Optional description used in social sharing.
   */
  description?: string;

  /**
   * Optional route used to build a custom URL for social sharing.
   * Since this is usually apparent from the current page route, setting this explicitly is not often necessary.
   */
  route?: string;

  /**
   * Canonical URL for this page. Absolute URLs are left as-is; relative paths are resolved with `DevsiteEnv.DEVSITE_URL`.
   * When set, `og:url` uses the same value so the link tag and Open Graph do not conflict.
   * Whitespace-only values are ignored so `route` can still supply `og:url`.
   */
  canonical?: string;

  /**
   * Optional custom image used in social sharing. Does not support blobs.
   * Relative and CMS-style paths are resolved to absolute URLs via `DevsiteEnv.DEVSITE_URL`.
   */
  image?: string;

  /**
   * Optional JSON-LD objects (e.g. FAQPage) emitted as `application/ld+json` script tags in the document head.
   */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];

  /**
   * When true, the title is rendered exactly as supplied without the " | Workday Developers" suffix.
   * Use for titles that already include the brand (e.g. the root SEO default).
   */
  useExactTitle?: boolean;

  /**
   * Open Graph object type. Documentation pages pass `article` so the tag matches the server shell,
   * and Twitter title and description are emitted alongside it. Other pages stay `website`.
   */
  ogType?: 'website' | 'article';
}

/**
 * Sets metadata about the current page, used to set browser titles as well as OpenGraph/Twitter header metadata for sharing.
 */
const DevsiteMetadata: React.FC<DevsiteMetadataProps> = ({
  title,
  parentTitle,
  description,
  route,
  canonical,
  image,
  jsonLd,
  useExactTitle = false,
  ogType = 'website',
}) => {
  const titleWithAppTitle = parentTitle ? title + separator + parentTitle : title;
  const titleWithSiteSuffix = useExactTitle ? titleWithAppTitle : titleWithAppTitle + suffix;

  const canonicalInput = canonical != null && canonical.trim().length > 0 ? canonical.trim() : undefined;

  const canonicalNeedsBase = Boolean(canonicalInput && hrefNeedsSiteBase(canonicalInput));
  const imageNeedsBase = Boolean(image && hrefNeedsSiteBase(image));
  const routeMayNeedBase = Boolean(route) && (!canonicalInput || hrefNeedsSiteBase(canonicalInput));
  const needsSiteUrl = canonicalNeedsBase || imageNeedsBase || routeMayNeedBase;
  const baseUrl = needsSiteUrl && DevsiteEnv?.DEVSITE_URL ? DevsiteEnv.DEVSITE_URL.replace(/\/$/, '') : undefined;

  const resolvedCanonical = canonicalInput ? resolveDevsiteAbsoluteUrl(canonicalInput, baseUrl) : undefined;
  const resolvedOgUrl =
    resolvedCanonical ?? (route && baseUrl ? `${baseUrl}${route.startsWith('/') ? route : `/${route}`}` : undefined);
  const resolvedImage = image ? resolveDevsiteAbsoluteUrl(image, baseUrl) : undefined;

  const jsonLdBlocks = jsonLd == null ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  return (
    <Helmet>
      <title>{titleWithSiteSuffix}</title>
      <meta property='og:title' content={titleWithSiteSuffix} />

      {description && <meta name='description' content={description} />}
      {description && <meta property='og:description' content={description} />}

      <meta property='og:type' content={ogType} />
      <meta name='twitter:card' content='summary' />
      <meta name='twitter:site' content='@Workday' />
      {ogType === 'article' && <meta name='twitter:title' content={titleWithSiteSuffix} />}
      {ogType === 'article' && description && <meta name='twitter:description' content={description} />}

      {resolvedCanonical && <link rel='canonical' href={resolvedCanonical} />}

      {resolvedImage && <meta property='og:image' content={resolvedImage} />}
      {resolvedImage && <meta name='twitter:image' content={resolvedImage} />}

      {resolvedOgUrl && <meta property='og:url' content={resolvedOgUrl} />}

      {jsonLdBlocks.map((block) => (
        <script key={jsonLdKey(block)} type='application/ld+json'>
          {serializeJsonLd(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default DevsiteMetadata;
